import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useForm, useWatch, FormProvider } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import dayjs from 'dayjs';
import AsyncSelectCreatable from 'react-select/async-creatable';
import Select from 'react-select';
import { CompanyNode, UpdateExperienceMutation } from 'generated/types';
import {
  ControlComponent,
  ICreatableNewOption,
  MenuComponent,
  OptionComponent,
  PlaceholderComponent
} from 'components/select';
import { AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';

import { DateType, months, yearsRange } from 'utils/date';
import { useCreateNewCompanyMutation, useLoadCompaniesMutation, useUpdateExperienceMutation } from 'generated/hooks';
import { z } from 'zod';
import { useAdditionalFormSpace } from '../_hooks/useAdditionalFormSpace';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useToast } from 'hooks/useToast';
import _ from 'lodash';
import { LoaderCircle } from 'lucide-react';
import { useTransientButton } from 'app/_components/useTransientButton';

export type ExperienceNodeFromBe = Omit<NonNullable<UpdateExperienceMutation['experience']>, '__typename'>;

export interface IExperienceForm {
  id: string | null;
  company: Omit<CompanyNode, '__typename' & 'direction'>;
  positionRaw: string;
  fromMonth: DateType | null;
  fromYear: DateType;
  toMonth: DateType | null;
  toYear: DateType | null;
  description: string;
  isCurrentlyWorkingHere: boolean,
  localId?: number;
}

interface IExperienceCard {
  companies: CompanyNode[];
  experienceItem: IExperienceForm & { localId?: number };
  index: number;
  isLastCard: boolean;
  isOpen: boolean;
  onUpdateItem: (experience: IExperienceForm, experienceFromBE: ExperienceNodeFromBe) => void;
  onDelete: (experience: IExperienceForm) => void;
  onSetDirtyCard: (id: string, isDirty: boolean) => void;
}

const experienceSchema = z.object({
  id: z.string().nullable(),
  company: z.object({
    id: z.string(),
    name: z.string()
  }, { message: 'This field is required' }),
  positionRaw: z.string().min(1, 'Position is required'),

  fromMonth: z.object({
    title: z.string(),
    value: z.number()
  }).nullable(),
  fromYear: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).required({ value: true }),

  toMonth: z.object({
    title: z.string(),
    value: z.number()
  }).nullable(),
  toYear: z.object({
    title: z.string(),
    value: z.number()
  }, { message: 'This field is required' }).nullable(),

  description: z.string().min(1, 'Required'),
  isCurrentlyWorkingHere: z.boolean(),
  localId: z.number().optional()
}).superRefine((({ isCurrentlyWorkingHere, toMonth, toYear, fromYear, fromMonth }, ctx) => {

  if (!isCurrentlyWorkingHere && (!toYear || !toYear.value)) {
    ctx.addIssue({
      path: ['toYear'],
      message: 'Year is required if you do not currently work here',
      code: 'custom'
    });
  }

  if (toYear && toMonth && fromYear && fromMonth) {
    const fromDate = dayjs(`${fromYear.value}-${fromMonth.value}-01`);
    const toDate = dayjs(`${toYear.value}-${toMonth.value}-01`);

    if (toDate.isBefore(fromDate)) {
      ctx.addIssue({
        path: ['toMonth'],
        message: 'End date cannot be earlier than start date',
        code: 'custom'
      });
      ctx.addIssue({
        path: ['toYear'],
        message: 'End date cannot be earlier than start date',
        code: 'custom'
      });
    }
  }

  if (toYear && (toYear < fromYear)) {
    ctx.addIssue({
      path: ['toYear'],
      message: 'End date cannot be earlier than start date',
      code: 'custom'
    });
  }
}));

export const ExperienceCard: FC<IExperienceCard> = (props) => {
  const {
    companies,
    experienceItem,
    index,
    isLastCard,
    isOpen,
    onUpdateItem,
    onDelete,
    onSetDirtyCard
  } = props;

  const [experienceItemSaved] = useState<IExperienceForm>({
    ...experienceItem
  });
  const [companiesSaved, setSavedCompanies] = useState(companies);

  const [, loadCompanies] = useLoadCompaniesMutation();
  const [, createCompany] = useCreateNewCompanyMutation();
  const [updateExperienceState, updateExperience] = useUpdateExperienceMutation();

  const { handleErrors } = useFormErrorHandling();
  const { handleToast } = useToast();

  const { transientBtn, transientShow } = useTransientButton({
    initial: 'Save',
    transient: 'Saved'
  });

  const form = useForm<IExperienceForm>({
    mode: 'all',
    values: { ...experienceItemSaved },
    resolver: standardSchemaResolver(experienceSchema)
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setError,
    setValue,
    clearErrors
  } = form;

  const getOptions = useCallback((company?: CompanyNode | null) => {
    if (!company) {
      return companiesSaved;
    }

    return companiesSaved.findIndex(item => item.id === company.id) > -1 ? companiesSaved : [company, ...companiesSaved];
  }, [companiesSaved]);

  const handleSaveExperience = useCallback(async (experience: IExperienceForm) => {
    try {
      const response = await updateExperience({
        description: experience.description,
        toYear: experience.toYear ? experience.toYear.value : null,
        fromMonth: experience.fromMonth ? experience.fromMonth.value + 1 : null,
        fromYear: experience.fromYear!.value,
        toMonth: experience.toMonth ? (experience.toMonth.value + 1) : null,
        id: experience.id ?? null,
        position: experience.positionRaw,
        companyId: experience.company!.id
      });

      if (response.error) {
        handleToast('Oops! Something went wrong!', 'error');
        return handleErrors(response.error, setError);
      }
      if (!response.data?.updateExperience) {
        handleToast('Oops! Something went wrong!', 'error');
        return;
      }

      if (!!experience.localId) {
        onSetDirtyCard(experience.localId.toString(), false);
      } else if (!!experience.id) {
        onSetDirtyCard(experience.id, false);
      }

      onUpdateItem({
        ...experience,
        id: response.data?.updateExperience?.experience?.id ?? ''
      }, response.data.updateExperience.experience);

      transientShow();
    } catch (error) {
      console.error(error);
    }
  }, [handleErrors, handleToast, onSetDirtyCard, onUpdateItem, setError, transientShow, updateExperience]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      if (!data.company?.id) {
        const response = await createCompany({
          name: data.company.name
        });

        if (response.error) {
          console.error(response.error);

          return;
        }

        return handleSaveExperience({
          ...data,
          company: response.data?.createCompany?.company
        });
      }

      return handleSaveExperience(data);
    } catch (error) {
      console.error(error);
    }
  }, [createCompany, handleSaveExperience]);

  const formRef = useRef<HTMLFormElement>(null);
  const { handleFocus, handleBlur } = useAdditionalFormSpace(formRef);

  const experienceWatch = useWatch({
    control
  });

  const checkIsDirty = useCallback((value1: any, value2: any) => {
    return !_.isEqual(value1, value2);
  }, []);

  const isDirty = useMemo(() => checkIsDirty(experienceItem, experienceWatch), [checkIsDirty, experienceItem, experienceWatch]);

  const handleDelete = useCallback(async () => {
    onDelete(experienceWatch as IExperienceForm);
  }, [experienceWatch, onDelete]);

  useEffect(() => {
    if (experienceItem.id) {
      onSetDirtyCard(experienceItem.id, isDirty);
    } else if (!!experienceItem.localId) {
      onSetDirtyCard(experienceItem.localId.toString(), true);
    }
  }, [experienceItem.id, experienceItem.localId, index, isDirty, isValid, onSetDirtyCard]);

  return (
    <div className='flex flex-col gap-10'>
      {!!index && (
        <div className='border-b border-b-gray-200' />
      )}
      <AccordionItem
        value={(index + 1).toString()}
        className={`rounded-radius-4xl p-8 ${!isLastCard ? 'mb-10' : ''} ${isOpen ? 'shadow-3xl bg-white border-b-0' : '!bg-gray-25 border border-gray-200'}`}
        key={experienceItem.id || experienceItem.localId || index}
      >
        <AccordionTrigger className='!p-0 w-full gap-2'>
          <p className='text-[30px]/[38px] text-left truncate font-medium'>
            {experienceWatch.positionRaw ?? ''} {experienceWatch.positionRaw && experienceWatch.company?.name ? 'at' : ''} {experienceWatch.company?.name}
          </p>
        </AccordionTrigger>
        <AccordionContent>
          <FormProvider {...form}>
            <form
              className='flex flex-col gap-6 pt-2'
              ref={isLastCard ? formRef : undefined}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Controller
                render={({ field, formState: { errors } }) => (
                  <div className='flex flex-col gap-sm flex-1'>
                    <label
                      className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                      htmlFor={`company-select-${index}`}
                    >
                      Company name
                    </label>
                    <AsyncSelectCreatable
                      inputId={`company-select-${index}`}
                      isClearable
                      cacheOptions
                      isSearchable
                      className='w-full'
                      defaultOptions={getOptions(experienceItem.company)}
                      getOptionLabel={(company: CompanyNode) => company?.name || (company as unknown as ICreatableNewOption)?.label || ''}
                      getOptionValue={(company: CompanyNode) => company.id}
                      createOptionPosition='first'
                      onCreateOption={(inputValue) => {
                        const newCompanyItem: CompanyNode = {
                          id: '',
                          name: inputValue,
                          description: null,
                          logo: null,
                          direction: null
                        };

                        setSavedCompanies(prevState => [...prevState, newCompanyItem]);
                        setValue('company', newCompanyItem, { shouldDirty: true, shouldValidate: true });
                      }}
                      loadOptions={(inputValue, callback) => {
                        try {
                          loadCompanies({
                            search: inputValue
                          }).then((response) => {
                            callback(response.data?.loadCompanies?.companies ?? []);
                          });

                        } catch (error) {
                          console.error(error);
                        }
                      }}
                      {...field}
                      components={{
                        Control: ControlComponent,
                        Option: OptionComponent,
                        Menu: MenuComponent,
                        Placeholder: PlaceholderComponent
                      }}
                      error={errors.company?.message}
                    />
                    {errors.company &&
                      <p className={`text-small text-error-600`}>{errors.company?.message}</p>
                    }
                  </div>
                )}
                name={`company`}
                control={control}
              />
              <Controller
                name={`positionRaw`}
                control={control}
                render={({ field: { value, ...field }, formState: { errors } }) => (
                  <Input
                    label='Position'
                    value={value ?? ''}
                    {...field}
                    errorText={errors.positionRaw?.message}
                  />
                )}
              />
              <div className='flex flex-col gap-15'>
                <div className='flex gap-15'>
                  <Controller
                    control={control}
                    name={`fromYear`}
                    render={({ field: { value, ...field }, formState: { errors } }) => (
                      <div className='flex flex-col gap-sm flex-1'>
                        <label
                          className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                          htmlFor={`from-year-select-${index}`}
                        >
                          Year Started
                        </label>
                        <Select
                          value={value}
                          placeholder='Select year'
                          inputId={`from-year-select-${index}`}
                          isClearable
                          isSearchable
                          className='w-full'
                          options={yearsRange()}
                          getOptionLabel={(year: DateType) => year.title}
                          getOptionValue={(year: DateType) => year.value.toString()}
                          {...field}
                          components={{
                            Control: ControlComponent,
                            Option: OptionComponent,
                            Menu: MenuComponent,
                            Placeholder: PlaceholderComponent
                          }}
                          error={errors.fromYear?.message}
                        />
                        {errors.fromYear &&
                          <p
                            className={`text-small text-error-600`}>{errors.fromYear?.message}</p>
                        }
                      </div>
                    )}
                  />
                  <Controller
                    render={({ field: { value, ...field }, formState: { errors } }) => (
                      <div className='flex flex-col gap-sm flex-1'>
                        <label
                          className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                          htmlFor={`from-month-select-${index}`}
                        >
                          Month Started
                        </label>
                        <Select
                          value={value}
                          placeholder='Select month'
                          inputId={`from-month-select-${index}`}
                          isClearable
                          isSearchable
                          className='w-full'
                          options={months}
                          getOptionLabel={(month: DateType) => month.title}
                          getOptionValue={(month: DateType) => month.value.toString()}
                          {...field}
                          components={{
                            Control: ControlComponent,
                            Option: OptionComponent,
                            Menu: MenuComponent,
                            Placeholder: PlaceholderComponent
                          }}
                          error={errors.fromMonth?.message}
                        />
                        {errors.fromMonth &&
                          <p
                            className={`text-small text-error-600`}>{errors.fromMonth?.message}</p>
                        }
                      </div>
                    )}
                    name={`fromMonth`}
                    control={control}
                  />
                </div>
                {!experienceWatch.isCurrentlyWorkingHere && (
                  <div className='mt-25'>
                    <div className='flex gap-15'>
                      <Controller
                        control={control}
                        name={`toYear`}
                        render={({ field: { value, ...field }, formState: { errors } }) => (
                          <div className='flex flex-col gap-sm flex-1'>
                            <label
                              className={`font-medium text-small max-w-max flex text-gray-900 relative`}
                              htmlFor={`from-year-select-${index}`}
                            >
                              Through
                            </label>
                            <Select
                              value={value}
                              placeholder='Select year'
                              inputId={`to-year-select-${index}`}
                              isClearable
                              isSearchable
                              className='w-full'
                              options={yearsRange()}
                              getOptionLabel={(year: DateType) => year.title}
                              getOptionValue={(year: DateType) => year.value.toString()}
                              {...field}
                              components={{
                                Control: ControlComponent,
                                Option: OptionComponent,
                                Menu: MenuComponent,
                                Placeholder: PlaceholderComponent
                              }}
                              error={errors.toYear?.message}
                            />
                            {errors.toYear &&
                              <p
                                className={`text-small text-error-600`}>{errors.toYear?.message}</p>
                            }
                          </div>
                        )}
                      />
                      <Controller
                        name={`toMonth`}
                        control={control}
                        render={({ field: { value, ...field }, formState: { errors } }) => (
                          <div className='flex flex-col gap-sm flex-1'>
                            <label
                              className={`font-medium text-small max-w-max flex text-gray-900 relative h-[20px]`}
                              htmlFor={`to-month-select-${index}`}
                            />
                            <Select
                              value={value}
                              placeholder='Select month'
                              inputId={`to-month-select-${index}`}
                              isClearable
                              isSearchable
                              className='w-full'
                              options={months}
                              getOptionLabel={(month: DateType) => month.title}
                              getOptionValue={(month: DateType) => month.value.toString()}
                              {...field}
                              components={{
                                Control: ControlComponent,
                                Option: OptionComponent,
                                Menu: MenuComponent,
                                Placeholder: PlaceholderComponent
                              }}
                              error={errors.toMonth?.message}
                            />
                            {errors.toMonth &&
                              <p
                                className={`text-small text-error-600`}>{errors.toMonth?.message}</p>
                            }
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
                <Controller
                  control={control}
                  name='isCurrentlyWorkingHere'
                  render={({ field: { value, onChange, ...field } }) => (
                    <label className='font-medium font-poppins text-gray-700 text-[14px]/[20px] flex items-center'
                           htmlFor={`currently_work_here_${index}`}>
                      <Checkbox
                        id={`currently_work_here_${index}`}
                        className='mr-2'
                        checked={value}
                        onCheckedChange={(checked) => {
                          clearErrors(['toMonth', 'toYear']);
                          onChange(checked);
                        }}
                        {...field}
                      />
                      I currently work here
                    </label>
                  )}
                />
              </div>

              <Controller
                control={control}
                name={`description`}
                render={({ field: { onBlur, value, ...field }, formState: { errors } }) => (
                  <Textarea
                    label='Key Responsibilities & Achievements'
                    rows={6}
                    placeholder='What were your key responsibilities and what achievements are you proud of?'
                    errorText={errors.description?.message || errors.root?.message || ''}
                    {...field}
                    value={value ?? ''}
                    onFocus={handleFocus}
                    onBlur={() => handleBlur(onBlur)}
                  />
                )}
              />
              <div className='flex justify-between items-center'>
                <Button variant='nakedGrayLink' className='p-0' type='button' onClick={handleDelete}>
                  Delete Card
                </Button>
                <Button variant='primaryBrand' size='small' className='px-[60px] ml-auto gap-3'
                        onClick={handleSubmit(onSubmit)}>
                  {updateExperienceState.fetching && (
                    <LoaderCircle className='animate-spin' />
                  )}
                  {transientBtn}
                </Button>
              </div>
            </form>
          </FormProvider>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};
